import React, { useEffect, useMemo, useState } from 'react';
import Scores from './costing.json';
import { Link } from 'react-router-dom';

function getDistinctValues(data, key) {
  const result = new Set();
  function traverse(obj) {
    for (const [k, value] of Object.entries(obj)) {
      if (Array.isArray(value) && k === key) {
        value.forEach(item => result.add(item));
      } else if (typeof value === 'object' && value !== null) {
        traverse(value);
      }
    }
  }
  traverse(data);
  return Array.from(result);
}

function findCategoryForRole(role) {
  for (const country of Object.values(Scores.data)) {
    for (const [category, categoryData] of Object.entries(country.data)) {
      if (categoryData.roles.includes(role)) return category;
    }
  }
  return "";
}

let lastSelections = null;
let lastScores = [{}, {}, {}];

function CostCalculatorComponent() {
  const [selection, setSelection] = useState({
    country: "USA",
    category: "",
    role: "",
    technology: "",
    experience: "",
  });
  const [roles, setRoles] = useState(getDistinctValues(Scores, 'roles'));

  const [theScores, setTheScores] = useState(lastScores[0]?.country ? lastScores : (() => {
    selectToChangeTheScores(0).then(({ myScore, newRoles, newSelections }) => {
      setSelection(prev => ({ ...prev, ...newSelections }));
      setRoles(newRoles);
      setTheScores(myScore);
    });
    return lastScores;
  })());

  const categories = useMemo(() => getDistinctValues(Scores, 'categories'), []);
  const experiences = useMemo(() => getDistinctValues(Scores, 'experiences'), []);

  function findMaxScores(scores) {
    return {
      MaxBTFScoreInPounds: Math.max(...scores.map(score => score?.BTFScoreInPounds || 0)),
      MaxSScoreInPounds: Math.max(...scores.map(score => score?.SScoreInPounds || 100)),
      MaxTScoreInPounds: Math.max(...scores.map(score => score?.TScoreInPounds || 100)),
    };
  }

  const [showFilteredScores, setShowFilteredScores] = useState([theScores[0], theScores[theScores.length - 1]]);
  const [maxScores, setMaxScores] = useState(findMaxScores(showFilteredScores));
  const [selectedValues, setSelectedValues] = useState(theScores[0]);

  let minHeight = 20
  let maxHeight = 60;
  let unitHeight = maxHeight / maxScores.MaxTScoreInPounds

  async function selectToChangeTheScores(x, choice, field) {
    let myScore = lastScores;
    let selections = { category: "", role: "", technology: "", experience: "" };
    let newRoles = [];
    let changeInRoles = false;

    if (x === 0) {
      Scores.Countries.forEach((country, index) => {
        const countryData = Scores.data[country];
        const category = countryData.categories[0];
        const role = countryData.data[category].roles[0];
        const experience = countryData.data[category].data[role].experiences[0];

        myScore[index] = {
          country,
          flag: countryData.flag,
          currency: countryData.currency,
          category,
          role,
          experience,
          ...countryData.data[category].data[role].data[experience].data,
        };

        if (index === 0) {
          selections = { category, role, experience };
          newRoles = countryData.data[category].roles;
        }
      });
    } else {
      selections = { ...lastSelections, [field]: choice };
      let category = findCategoryForRole(selections.role);
      if (field === "role" && !Scores.data[myScore[0].country].data[selections.category]?.data[selections.role]) {
        selections.category = category;
        changeInRoles = true;
        newRoles = Scores.data[myScore[0].country].data[selections.category].roles;
      } else if (field === "category" && category !== choice) {
        changeInRoles = true;
        newRoles = Scores.data[myScore[0].country].data[selections.category].roles;
        selections.role = newRoles[0];
      }

      myScore.forEach((score, index) => {
        myScore[index] = {
          ...score,
          ...Scores.data[score.country].data[selections.category].data[selections.role].data[selections.experience].data,
          ...selections,
        };
      });
    }

    lastSelections = { ...selections };
    lastScores = myScore;
    return { myScore, changeInRoles, newRoles, newSelections: selections };
  }

  const DynamicGenerateSelect = ({ values, text, myClass }) => {
    const handleChange = async (e) => {
      const newValue = e.target.value;
      const { myScore, changeInRoles, newRoles, newSelections } = await selectToChangeTheScores(1, newValue, text);
      setSelection(prev => ({ ...prev, ...newSelections }));
      if (changeInRoles) setRoles(newRoles);
      const selectedScore = myScore.find(score => score.country === selection.country);
      setTheScores(myScore);
      setSelectedValues(selectedScore);
      setShowFilteredScores([selectedScore, myScore[myScore.length - 1]]);
      setMaxScores(findMaxScores([selectedScore, myScore[myScore.length - 1]]));
    };

    return (
      <div className={`select-your-${text} ${myClass}`}>
        <label htmlFor={`${text}-select`} className="block text-white mqAllDesktop:font-bold font-semibold mb-2 capitalize">
          Select&nbsp;your&nbsp;{text}
        </label>
        <div className="relative">
          <select
            id={`${text}-select`}
            className="block appearance-none w-full bg-gray-700 border border-gray-600 text-white py-2 px-3 pr-8 rounded leading-tight focus:outline-none focus:ring focus:ring-purple-500"
            value={selection[text]}
            onChange={handleChange}
          >
            {values?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg className="fill-current h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M5.516 7.548l4.482 4.482 4.482-4.482-1.034-1.034-3.448 3.448-3.448-3.448z" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="bg-gradient-to-br from-[#1c013d] to-[#2e0249] p-8 font-montserrat z-[100] flex flex-col mqAllDesktop:mx-auto mqAll:mx-auto mq750:mx-4 gap-8 mq1157:mx-auto mqAllMobile:p-2 mq400:px-3 items-center mx-[10px] mqAllMobile:mb-10 max-w-[1114px]">
      <div className='text-32xl font-bold text-white font-montserrat mq825:text-15xl mq550:!text-12xl mq450:!text-4xl mq450:w-[300px] mqAllMobile:!text-2xl mqAllMobile:mt-3 text-gray-900-1 px-4 rounded-2xl box-border border-solid text-gray-900-1 !w-full'>
        <div className='text-center'>
          Calculate what you can save
        </div>
        <div className="text-2xl pt-2 font-bold text-center text-gray-900-1 mqAllMobile:text-smi">For a {showFilteredScores[0].country} based {selection.experience} {selection.role} you can save upto £{showFilteredScores[0].savings}k!</div>
      </div>
      <div className='flex flex-wrap'>
        <div className="flex flex-col mq650:w-full w-full min-w-[403px] mqAllMobile:min-w-[300px]">
          <div className='select-container-box p-4 mqAllMobile:px-1 bg-gray-800 rounded-lg shadow-lg gap-3 mq400:gap-2 grid grid-cols-6 mqAllMobile:grid-cols-6 grid-flow-row text-xl mq750:!text-mid mq450:!text-smi'>
            <div className="select-your-country col-start-1 mq825:col-span-3 mq825:row-[span_3_/_span_2] col-span-2 row-span-2 self-stretch flex flex-col">
              <label className="block text-white mqAllDesktop:font-bold font-semibold mb-2">Select&nbsp;your&nbsp;Country</label>
              <div className="flex mqAllMobile:flex-col items-center gap-2 mqAllMobile:justify-center justify-start w-full h-full">
                {theScores?.filter((score, i) => i !== theScores.length - 1)
                  .map((score, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedValues(score);
                        setSelection((prev) => ({ ...prev, country: score.country }))
                        setShowFilteredScores([score, theScores[theScores.length - 1]]);
                        setMaxScores(findMaxScores([score, theScores[theScores.length - 1]]));
                      }}
                      className={`py-2 px-4 rounded-lg font-semibold mqAllMobile:w-[120px] ${selectedValues.country === score.country
                        ? "bg-purple-500 text-white"
                        : "bg-gray-700 text-gray-300"
                        }`}
                    >
                      <img
                        src={score.flag}
                        alt={`${score.country} Flag`}
                        className="w-14 h-14 mq425:w-10 mq425:h-10 rounded-full"
                      />
                      {score.country}
                    </button>
                  ))}
              </div>
            </div>
            <DynamicGenerateSelect text={"category"} values={categories} myClass="mq825:col-start-4 mq825:col-span-3 col-start-3 col-span-2" />
            <DynamicGenerateSelect text={"role"} values={roles} myClass="mq825:col-start-4 mq825:col-span-3 col-start-5 col-span-2" />
            <DynamicGenerateSelect text={"experience"} values={experiences} myClass="mq825:col-start-4 mq825:col-span-3 col-start-3 col-span-2" />
          </div>
          <div className="w-full border-t my-4 border-gray-700 bg-gradient-to-r from-[#c471ed] to-[#a044ff]">
            {/* <div className="text-2xl py-2 font-bold text-center text-gray-900-1 mqAllMobile:text-smi">You can save upto £{showFilteredScores[0].savings}k with {((showFilteredScores[0].savings / showFilteredScores[0].BTFScoreInPounds) * 100).toFixed(2)}% Cost Reduction</div> */}
            <div className="text-2xl py-2 font-bold text-center text-gray-900-1 mqAllMobile:text-smi">You are getting a {Math.round((showFilteredScores[0].savings / showFilteredScores[0].BTFScoreInPounds) * 100)}% Cost Reduction</div>
            {/* <div className="text-2xl py-2 font-bold text-center text-gray-900-1 mqAllMobile:text-smi">For {showFilteredScores[0].country} you can save upto £{showFilteredScores[0].savings}k for {selection.role} at {selection.experience}</div> */}
          </div>
          <div className="grid grid-cols-2">
            {showFilteredScores.map((score, index) => {
              let calcBFTscore = unitHeight ? (minHeight + (unitHeight * score.BTFScoreInPounds)) : undefined
              // let calcSscore = unitHeight ? (minHeight + (unitHeight * score.SScoreInPounds)) : undefined
              if (score.country !== "VCT") {
                maxHeight = calcBFTscore
                calcBFTscore = calcBFTscore / maxHeight * 180
              } else {
                calcBFTscore = calcBFTscore / maxHeight * 180
                maxHeight = 1
              }
              return (<div
                key={index}
                className={`bg-gray-800 h-[210px] min-h-[220px] relative p-6 mqAllMobile:p-3 ${index === 0 ? 'rounded-l-lg' : ''} ${index === theScores.length - 1 ? 'rounded-r-lg' : ''} border-gray-700`}
              >
                <div className="flex items-center justify-center">
                  <img src={score.flag} alt={`${score.country} Flag`} className="w-8 h-8 mr-2 rounded-8xl" />
                  <h2 className="text-2xl font-bold text-white">{score.country}</h2>
                </div>
                <div className='w-full bottom-2 ml-[-24px] mqAllMobile:ml-[-12px] absolute'>
                  <div className="flex flex-col mb-3 mx-8 mq400:mx-6">
                    <div className="values text-center">
                      <div className={`value-top h-20 bg-pink-500 rounded-t-lg flex justify-center items-center transition-all duration-500 ease-in-out`}
                        style={calcBFTscore && { height: `${minHeight / 3 + Math.floor(calcBFTscore * 20 / 100)}px` }}>
                        <p className="text-xl text-gray-200 mqAllMobile:text-smi">
                          <b>£{Math.floor(score.BTFScoreInPounds * 20 / 100)}k {score.BTFOptionalScoreInPounds ? `(${score.currency}${Math.floor(score.BTFOptionalScoreInPounds * 20 / 100)}k)` : ""}</b>
                        </p>
                      </div>
                      <div className={`value-bot h-20 bg-[#64B5F6] rounded-b-lg flex justify-center items-center transition-all duration-500 ease-in-out`}
                        style={calcBFTscore && { height: `${Math.ceil(calcBFTscore * 80 / 100)}px` }}>
                        <p className="text-xl text-gray-200 mqAllMobile:text-smi">
                          <b>£{Math.ceil(score.BTFScoreInPounds * 80 / 100)}k {score.BTFOptionalScoreInPounds ? `(${score.currency}${Math.ceil(score.BTFOptionalScoreInPounds * 80 / 100)}k)` : ""}</b>
                        </p>
                      </div>
                      {/* <div className={`h-20 bg-[#64B5F6] rounded-b-lg flex justify-center items-center transition-all duration-500 ease-in-out`}
                      style={calcSscore && { height: `${calcSscore}px` }}>
                      <p className="text-xl text-gray-200 mqAllMobile:text-smi">
                        <b>£{score.SScoreInPounds}k {score.SOptionalScoreInPounds ? `(${score.currency}${score.SOptionalScoreInPounds}k)` : ""}</b>
                      </p>
                    </div> */}
                    </div>
                  </div>
                  <div className="text-center mx-auto">
                    {/* <p className="text-3xl font-bold text-white mb-0 mqAllMobile:text-mid mq400:text-[15px]">£{score.TScoreInPounds}k {score.TOptionalScoreInPounds ? `(${score.currency}${score.TOptionalScoreInPounds}k)` : ""}</p> */}
                    <div className='flex items-center justify-center min-h-[26px]'>
                      {/* <p className="mqAllMobile:text-smi text-[#e5e5e5]">{score.savings ? (<b>£{score.savings}k savings</b>) : <b>Best Choice</b>}</p> */}
                      <p className="mqAllMobile:text-smi text-[#e5e5e5] mqAllDesktop:text-xl"><b>Total: £{score.BTFScoreInPounds}k</b></p>
                      {score.savings === 0 && (
                        <img src="/checked-6-1@2x.png" alt="Best" className="w-[25px] h-[26px] ml-2" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )
            })}
          </div>
          {/* <div className="w-full rounded-b-lg border-t border-gray-700 bg-gradient-to-r from-[#c471ed] to-[#a044ff]">
            <div className="text-2xl py-2 font-bold text-center text-gray-900-1 mqAllMobile:text-smi">£{showFilteredScores[0].savings}k&nbsp;savings,&nbsp;{((showFilteredScores[0].savings/showFilteredScores[0].BTFScoreInPounds)*100).toFixed(2)}%&nbsp;Cost Reduction ({showFilteredScores[0].country})</div>
          </div> */}
          <div className="flex-wrap justify-center items-center mt-4 bg-opacity-20 bg-blue-900 text-blue-400 transition-all rounded">
            <div className="mr-6 flex items-center rounded-lg">
              <span className="bg-pink-500 w-4 h-4 rounded-full inline-block mr-2"></span>
              <span className="text-sm text-white">Benefits + Taxes + Fees</span>
            </div>
            <div className="flex mr-6 items-center rounded-lg">
              <span className="bg-[#64B5F6] w-4 h-4 rounded-full inline-block mr-2"></span>
              <span className="text-sm text-white">Salary</span>
            </div>
          </div>
        </div>
        <div className='relative bg-gray-800 rounded-lg px-[20px] mqAllMobile:px-2 w-full'>
          <div className="text-center flex-col mq1157:mt-4">
            <h2 className="text-2xl font-bold text-white text-left mqAllMobile:text-lg">The best of the best are now in your budget.</h2>
            <p className="text-[#e5e5e5] mt-2 text-left text-[16px] mqAllMobile:text-smi">
              As industry experts, we specialise in finding and hiring the exact talent or
              team your project needs. Itʼs as simple as sharing your team requirements
              with us. If you're unsure, we offer quick consultation sessions to help
              identify the ideal team for your project. We handle the entire
              process—recruitment, interviews, providing hardware, <b>office space (so
                your team can be together)</b>, payroll, and all contractual work—so you
              donʼt need to invest in building a recruitment team or managing
              administrative overhead like national insurance and other fees.<br /><br />
              By partnering with our <b>UK-based company</b>, you avoid the complexities of
              international operations. Youʼll have a straightforward contract between
              two companies for a defined term, ensuring compliance with UK
              regulations and a hassle-free experience. Plus, by outsourcing globally, we
              help you optimise costs and achieve the best value for your investment.
            </p>
          </div>
        </div>
        <div className='w-full flex flex-row justify-center mb-[-8px] mqAllMobile:mb-2'>
          <button className="cursor-pointer [border:none] bg-[transparent] w-[176.2px] flex flex-row items-center justify-center z-50">
            <Link to="/contact" className="no-underline flex-1 rounded-[55.08px] [background:linear-gradient(132.03deg,_#c471ed,_#a044ff)] overflow-hidden flex flex-row items-start justify-start px-[22px] py-[11px] whitespace-nowrap">
              <span className="text-lg text-white text-center w-full">Book a Call</span>
            </Link>
          </button>
        </div>
      </div>
    </section>
  );
}

export default CostCalculatorComponent;
